const INVOLVEME_LOADER_ID = 'br-involveme-embed-loader';
const INVOLVEME_LOADER_URL = 'https://brainritual.involve.me/embed';
const INVOLVEME_PROJECT = 'brain-energy-quiz-v1';
const QUIZ_SELECTOR = '.br-involveme-quiz .involveme_embed[data-project="brain-energy-quiz-v1"]';
const FAILURE_TIMEOUT = 15000;
const FALLBACK_ATTRIBUTION_PARAMETERS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'snowball',
];
const failureTimers = new WeakMap();

function getQuizFallback(mount) {
  return mount.closest('.br-involveme-quiz')?.querySelector('[data-br-involveme-fallback-message]');
}

function markQuizReady(mount) {
  window.clearTimeout(failureTimers.get(mount));
  failureTimers.delete(mount);
  mount.classList.add('is-ready');
  mount.classList.remove('is-error');
  mount.setAttribute('aria-busy', 'false');

  const fallback = getQuizFallback(mount);
  if (fallback) fallback.hidden = true;
}

function markQuizError(mount) {
  if (mount.querySelector('iframe')) return;

  window.clearTimeout(failureTimers.get(mount));
  failureTimers.delete(mount);
  mount.classList.add('is-error');
  mount.setAttribute('aria-busy', 'false');

  const fallback = getQuizFallback(mount);
  if (fallback) fallback.hidden = false;
}

function scheduleQuizFailure(mount) {
  if (failureTimers.has(mount)) return;

  const loading = mount.querySelector('[data-br-involveme-loading]');
  if (loading) loading.hidden = false;

  failureTimers.set(
    mount,
    window.setTimeout(() => markQuizError(mount), FAILURE_TIMEOUT)
  );
}

function watchForQuizIframe(mount) {
  if (mount.querySelector('iframe') || mount.dataset.brIframeObserver === 'active') return;

  mount.dataset.brIframeObserver = 'active';
  const observer = new MutationObserver(() => {
    if (!mount.querySelector('iframe')) return;

    markQuizReady(mount);
    delete mount.dataset.brIframeObserver;
    observer.disconnect();
  });

  observer.observe(mount, { childList: true, subtree: true });
}

function preserveFallbackParameters(root = document) {
  const source = new URLSearchParams(window.location.search);

  root.querySelectorAll?.('[data-br-involveme-fallback]').forEach((link) => {
    const destination = new URL(link.href);

    for (const parameter of FALLBACK_ATTRIBUTION_PARAMETERS) {
      destination.searchParams.delete(parameter);
      for (const value of source.getAll(parameter)) {
        destination.searchParams.append(parameter, value);
      }
    }

    link.href = destination.toString();
  });
}

function initializeQuiz(mount) {
  if (mount.querySelector('iframe')) {
    markQuizReady(mount);
    return;
  }

  if (!window.brandquizEmbed) return;

  watchForQuizIframe(mount);

  // Involve.me remembers embedded project slugs globally. Theme Editor section
  // replacement removes the iframe but not that memory, so release only this
  // exact project before asking the supported loader to scan the new section.
  if (Array.isArray(window.brandquizEmbed.embeds)) {
    window.brandquizEmbed.embeds = window.brandquizEmbed.embeds.filter(
      (project) => project !== INVOLVEME_PROJECT
    );
  }

  window.brandquizEmbed.init();

  const iframe = mount.querySelector('iframe');
  if (iframe) markQuizReady(mount);
}

function initializeAllQuizzes(root = document) {
  const mounts = root.matches?.(QUIZ_SELECTOR)
    ? [root]
    : Array.from(root.querySelectorAll?.(QUIZ_SELECTOR) || []);

  if (!mounts.length) return;

  preserveFallbackParameters(root);
  mounts.forEach(scheduleQuizFailure);

  if (window.brandquizEmbed) {
    mounts.forEach(initializeQuiz);
    return;
  }

  let loader = document.getElementById(INVOLVEME_LOADER_ID);

  if (!loader) {
    loader = document.createElement('script');
    loader.id = INVOLVEME_LOADER_ID;
    loader.src = INVOLVEME_LOADER_URL;
    loader.async = true;
    document.head.appendChild(loader);
  }

  loader.addEventListener('load', () => mounts.forEach(initializeQuiz), { once: true });
  loader.addEventListener('error', () => mounts.forEach(markQuizError), { once: true });
}

if (!window.brInvolvemeQuiz) {
  window.brInvolvemeQuiz = { initialize: initializeAllQuizzes };

  document.addEventListener('shopify:section:load', (event) => {
    window.brInvolvemeQuiz.initialize(event.target);
  });
}

window.brInvolvemeQuiz.initialize(document);
